import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Hash, X } from "lucide-react";
import { create } from "zustand";
import { searchService } from "@/data/search/client";
import { usePalette } from "@/store/palette";
import { IconButton } from "@/ui/IconButton";
import s from "./TrashDialog.module.css";
import t from "./TagBrowser.module.css";

interface TagBrowserStore {
  open: boolean;
  setOpen: (open: boolean) => void;
}
export const useTagBrowser = create<TagBrowserStore>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

/** Every tag in the workspace (item tags + inline #hashtags), with counts. */
export function TagBrowser() {
  const { open, setOpen } = useTagBrowser();
  const [tags, setTags] = useState<[string, number][]>([]);
  const openPalette = usePalette((p) => p.open);

  useEffect(() => {
    if (!open) return;
    void searchService.allTags().then((counts) => {
      setTags([...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])));
    });
  }, [open]);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.dialog}>
          <div className={s.header}>
            <Dialog.Title className={s.title}>Tags</Dialog.Title>
            <Dialog.Close asChild>
              <IconButton label="Close">
                <X size={14} />
              </IconButton>
            </Dialog.Close>
          </div>
          <div className={s.list}>
            {tags.length === 0 ? (
              <div className={s.empty}>
                No tags yet. Add them in the note info panel or type #tag anywhere in a note.
              </div>
            ) : (
              <div className={t.cloud}>
                {tags.map(([tag, count]) => (
                  <button
                    key={tag}
                    type="button"
                    className={t.tag}
                    onClick={() => {
                      setOpen(false);
                      openPalette("switcher", `#${tag}`);
                    }}
                  >
                    <Hash size={11} />
                    {tag}
                    <span className={t.count}>{count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
